import { MappableVendorValues, PaymentTerms, VendorRequestMapPreview, VendorType } from "@/interfaces";
import { DEFAULT_CACHE_TIME } from "@/utils/constants";
import { useQuery } from "@tanstack/react-query";
import type { SortConfig } from "@/components/DataTable/types";
import api from "./api";
import { serializeFilterParams } from "./queryParams";

export interface MapVendorRequestToNetSuitePayload {
  netsuiteVendorId: string;
  netsuiteName?: string | null;
  companyId?: string;
  displayName: string;
  paymentTerm?: PaymentTerms | null;
  vendorUpdates?: Partial<MappableVendorValues>;
}

/**
 * Vendor request payloads for frontend
 */
export interface CreateVendorRequest {
  vendorName: string;
  vendorLegalName?: string;
  type?: "individual" | "company";
  title?: string;
  firstName?: string;
  lastName?: string;
  domain?: string;
  category?: string;
  currency?: string;
  vendorType?: "purchase" | "expense";
  address?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pin?: string;
  panNumber?: string;
  gstNumber?: string;
  notes?: string;
  bankName?: string;
  accountNumber?: string;
  accountHolderName?: string;
  ifscCode?: string;
  accountType?: "Savings" | "Current";
  routingNumber?: string;
  swiftCode?: string;
  companies?: string[]; // company ids
  adminEmail?: string;
  password?: string; // Optional temporary password for SSO
  isActive?: boolean;
}

export type UpdateVendorRequest = Partial<CreateVendorRequest>;

export interface PaginatedVendorsResponse {
  vendors: VendorType[];
  total: number;
  page: number;
  limit: number;
}

/**
 * React Query hook for fetching vendors assigned to a company
 */
export const useGetVendorsByCompanyId = (companyId: string | null) => {
  return useQuery<{
    data: VendorType[];
    total: number;
  }>({
    queryKey: ["company", companyId, "vendors"],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID is required");
      const response = await api.get(`/company/${companyId}/vendors`);
      // const response = await api.get(`/vendors`);
      console.log(response);

      return response.data;
    },
    enabled: !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook for fetching vendors across multiple companies (Finance view)
 */
export const useGetVendorsByCompanyIds = (companyIds: string[]) => {
  return useQuery<{
    data: VendorType[];
    total: number;
  }>({
    queryKey: ["companies", companyIds, "vendors"],
    queryFn: async () => {
      const response = await api.get("/company/vendors", {
        params: { companyIds: companyIds.join(",") },
      });
      return response.data;
    },
    enabled: companyIds.length > 0,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook for fetching all vendors
 */
export const useVendorsListAll = () => {
  return useQuery<PaginatedVendorsResponse>({
    queryKey: ["vendors", "all"],
    queryFn: async () => {
      const response = await api.get("/vendors");
      return response.data;
    },
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook for fetching all known vendor industry/category values,
 * including ones added via the "Other" option on the create/edit forms.
 */
export const useVendorCategories = () => {
  return useQuery<{ data: string[] }>({
    queryKey: ["vendors", "categories"],
    queryFn: async () => {
      const response = await api.get("/vendors/categories");
      return response.data;
    },
    staleTime: DEFAULT_CACHE_TIME,
  });
};


/**
 *
 * React Query hook to fetch clients by company ID and Service ID
 * @param serviceId
 * @Query
 * @returns
 */
export const useGetVendorsByCompanyIdandServiceId = (companyId: string, serviceId: string) => {
  return useQuery<{
    vendor: { id: string, vendorName: string }[];
    total: number;
  }>({
    queryKey: ["vendors", companyId, serviceId],
    queryFn: async () => {
      const response = await api.get(`/vendors/${serviceId}/vendors`, {
        params: { companyId },
      });
      return response.data;
    },
    staleTime: DEFAULT_CACHE_TIME,
    enabled: !!serviceId && !!companyId, // Only run when both IDs exist
  });
};


const vendorService = {
  getAll: async (
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      sort?: SortConfig;
      filters?: Record<string, unknown>;
      signal?: AbortSignal;
    },
    config?: Record<string, unknown>,
  ): Promise<PaginatedVendorsResponse | VendorType[]> => {
    const { page = 1, limit = 20, search, sort, filters, signal } = params || {};
    const serializedFilters = serializeFilterParams(filters);
    const resp = await api.get("/vendors", {
      params: {
        page,
        limit,
        search,
        sortField: sort?.field,
        sortOrder: sort?.order,
        ...serializedFilters,
      },
      signal,
      ...config,
    });
    // server returns { vendors, total, page, limit }
    return resp.data;
  },

  getById: async (id: string): Promise<VendorType> => {
    const resp = await api.get(`/vendors/${id}`, { params: { id } });
    return resp.data;
  },

  create: async (data: CreateVendorRequest) => {
    const resp = await api.post<{ message: string; data: VendorType }>("/vendors/create", data);
    return resp.data;
  },

  update: async (id: string, data: UpdateVendorRequest) => {
    const resp = await api.put<{ message: string; data: VendorType }>(`/vendors/${id}`, data);
    return resp.data;
  },

  updateStatus: async (id: string, isActive: boolean) => {
    const resp = await api.patch<{ message: string; data: VendorType }>(`/vendors/${id}/status`, { isActive });
    return resp.data;
  },

  /**
   * Assign services to client
   */
  assignServices: (vendorId: string, serviceIds: string[]) => {
    return api.post<{ message: string; data: VendorType }>(`/vendors/${vendorId}/services`, { serviceIds });
  },

  delete: async (id: string) => {
    const resp = await api.delete(`/vendors/${id}`, { params: { id } });
    return resp.data;
  },

  deleteVendor: async (id: string) => {
    const resp = await api.delete(`/vendors/${id}`, { params: { id } });
    return resp.data;
  },

  approveVendorRequest: async (vendorId: string, options?: { createInNetSuite?: boolean }) => {
    const resp = await api.post<{ data: VendorType; message?: string }>(`/vendors/${vendorId}/approve`, {
      createInNetSuite: options?.createInNetSuite ?? true,
    });
    return resp.data;
  },

  rejectVendorRequest: async (vendorId: string, reason: string) => {
    const resp = await api.post<{ data: VendorType; message?: string }>(`/vendors/${vendorId}/reject`, { reason });
    return resp.data;
  },

  /**
   * List pending vendor requests for Finance review
   */
  getPendingApprovals: async (params?: { page?: number; limit?: number; companyId?: string }) => {
    const resp = await api.get<{ data: VendorType[]; total: number; page: number; limit: number }>(
      "/vendors/pending-approvals",
      { params },
    );
    return resp.data;
  },

  /**
   * Check for duplicate vendors
   */
  checkDuplicates: async (vendorName: string, domain: string) => {
    const resp = await api.get<{ data: VendorType[]; message?: string }>("/vendors/duplicates", {
      params: { vendorName, domain },
    });
    return resp.data;
  },

  /**
   * Map an existing vendor to a company
   */
  mapToCompany: async (vendorId: string, companyId: string) => {
    const resp = await api.post<{ data: VendorType; message?: string }>("/vendors/map-to-company", {
      vendorId,
      companyId,
    });
    return resp.data;
  },

  /**
   * Upload an extra vendor document (PAN card, GST certificate, MSME certificate)
   */
  uploadVendorDocument: async (vendorId: string, type: string, formData: FormData) => {
    const resp = await api.post(`/vendors/${vendorId}/documents/${type}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return resp.data;
  },

  /**
   * Get a presigned URL for an extra vendor document
   */
  getVendorDocumentUrl: async (vendorId: string, type: string) => {
    const resp = await api.get(`/vendors/${vendorId}/documents/${type}/url`);
    return resp.data;
  },

  /**
   * Upload a custom, user-named vendor document (countries outside India/US)
   */
  uploadVendorCustomDocument: async (vendorId: string, formData: FormData) => {
    const resp = await api.post(`/vendors/${vendorId}/custom-documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return resp.data;
  },

  /**
   * Get presigned URL for a custom vendor document
   */
  getVendorCustomDocumentUrl: async (vendorId: string, docId: string) => {
    const resp = await api.get(`/vendors/${vendorId}/custom-documents/${docId}/url`);
    return resp.data;
  },

  /**
   * Delete a custom vendor document
   */
  deleteVendorCustomDocument: async (vendorId: string, docId: string) => {
    const resp = await api.delete(`/vendors/${vendorId}/custom-documents/${docId}`);
    return resp.data;
  },

  /**
   * Compare a pending vendor request with the record it would be mapped to via a NetSuite vendor
   */
  getVendorRequestMapPreview: async (vendorId: string, params: { netsuiteVendorId: string; companyId?: string }) => {
    const resp = await api.get<{ data: VendorRequestMapPreview; message?: string }>(`/vendors/${vendorId}/map-preview`, {
      params,
    });
    return resp.data.data;
  },

  /**
   * Finance "Map to Existing": map a pending vendor request to an existing NetSuite vendor,
   * applying the chosen field values and the company-level details
   */
  mapVendorRequestToNetSuiteVendor: async (vendorId: string, payload: MapVendorRequestToNetSuitePayload) => {
    const resp = await api.post<{ data: VendorType; message?: string }>(
      `/vendors/${vendorId}/map-to-netsuite-vendor`,
      payload,
    );
    return resp.data;
  },
};

export default vendorService;
